/** Client-side rule validation for the RulesEditor mounts.
 *
 * Fetches the backend's rule-args schema once per session and validates the
 * draft rows the way the server gates a save: issues on new or changed rows
 * BLOCK (the page refuses to save), issues on rows identical to the stored ones
 * are WARNINGS (legacy rules the server still accepts unchanged). A failed or
 * newer-than-supported schema means no client validation — the server stays
 * authoritative either way.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as rulesetsApi from '@/api/notificationRulesets';
import type { RuleWire } from '@/api/notificationRulesets';

import { rulesToWrite } from './ruleSerialize';
import { schemaSupported, validateRuleList, type RuleArgsSchema, type RuleIssue } from './ruleSchema';
import type { RuleDraft } from './ruleTypes';

export interface RuleIssueMaps {
  /** Row index → message (blocking). Same keying as the server's row errors. */
  errors: Record<string, string>;
  /** Row index → message (non-blocking). */
  warnings: Record<string, string>;
  blocking: boolean;
}

const NONE: RuleIssueMaps = { errors: {}, warnings: {}, blocking: false };

export function useRuleSchema(): RuleArgsSchema | null {
  const query = useQuery({
    queryKey: ['notification_rule_schema'],
    queryFn: () => rulesetsApi.ruleSchema(),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  const data = query.data as RuleArgsSchema | undefined;
  return schemaSupported(data) ? data : null;
}

function joined(byRow: Record<number, RuleIssue[]>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [idx, issues] of Object.entries(byRow)) out[idx] = issues.map((i) => i.message).join(' ');
  return out;
}

export function computeRuleIssues(
  schema: RuleArgsSchema | null,
  drafts: RuleDraft[],
  stored: RuleWire[] | undefined,
): RuleIssueMaps {
  if (!schema) return NONE;
  const baseline: Record<number, Record<string, unknown>> = {};
  for (const r of stored ?? []) baseline[r.id] = { ...r, nested_ruleset_id: r.nested_ruleset };
  const rows = rulesToWrite(drafts) as unknown as Record<string, unknown>[];
  const res = validateRuleList(schema, rows, baseline);
  return { errors: joined(res.errors), warnings: joined(res.warnings), blocking: res.blocking };
}

export function useRuleIssues(drafts: RuleDraft[], stored: RuleWire[] | undefined): RuleIssueMaps {
  const schema = useRuleSchema();
  return useMemo(() => computeRuleIssues(schema, drafts, stored), [schema, drafts, stored]);
}
